import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    Button,
    CircularProgress,
    Chip,
    Grid,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    MenuItem,
    Divider,
    IconButton,
    Tooltip,
    FormControlLabel,
    Checkbox,
    Switch,
    Autocomplete,
    createFilterOptions,
    FilterOptionsState,
} from '@mui/material';
import {
    Apartment,
    Close,
    People,
    TimerOff,
} from '@mui/icons-material';
import { getCurrentWeekNumber } from '../shared/utils';
import {
    format, startOfWeek,
    addDays
} from 'date-fns';
import { fr } from 'date-fns/locale';
import { DayDetails, EditDialogProps, Timesheet } from '../interfaces/timesheet';
import BonusDialog from './BonusDialog';
import { StarOutline } from '@mui/icons-material';
import { DayBonus } from '../interfaces/timesheet';
import { useUserRole } from '../context/userRoleContext';
import { DateTime } from 'luxon';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { makeRenderWeekDay } from './datePicker/renderWeekDay';

const createMultiFieldFilter = () => {
    const defaultFilterOptions = createFilterOptions();

    return (options: any[], state: FilterOptionsState<unknown>) => {
        if (!state.inputValue) {
            return defaultFilterOptions(options, state);
        }

        const searchText = state.inputValue.toLowerCase();
        return options.filter(option =>
            option.name.toLowerCase().includes(searchText) ||
            option.matricule.toString().toLowerCase().includes(searchText)
        );
    };
};

export const TimesheetEditDialog: React.FC<EditDialogProps> = ({ open, timesheet, onClose, onSave, masterData, userRole, isRefreshing }) => {
    const [editedTimesheet, setEditedTimesheet] = useState<Timesheet | null>(null);
    const isEditingAllowed = userRole && (
        (userRole === 'HR' && !timesheet?.foreman_validated) ||
        (userRole === 'HR' && timesheet?.foreman_validated) ||
        (userRole === 'Foreman' && !timesheet?.foreman_validated && !timesheet?.hr_validated) ||
        (userRole === 'Foreman' && timesheet?.foreman_validated && !timesheet?.hr_validated) ||
        (userRole === 'User' && !timesheet?.hr_validated && !timesheet?.foreman_validated)
    );
    const [hoveredDate, setHoveredDate] = useState<DateTime<true> | DateTime<false> | null>(null);

    const { isAdmin } = useUserRole()
    // Hours and quarters options
    const MAX_HOURS = 12;
    const hoursOptions = Array.from({ length: MAX_HOURS + 1 }, (_, i) => i);
    const quarterOptions = [0, 0.25, 0.50, 0.75];
    // Cannot edit if item is not synced or already validated
    // Weekday labels for display
    const weekdayLabels = {
        lundi: 'Lundi',
        mardi: 'Mardi',
        mercredi: 'Mercredi',
        jeudi: 'Jeudi',
        vendredi: 'Vendredi',
        samedi: 'Samedi',
        dimanche: 'Dimanche'
    };
    const multiFieldFilter = createMultiFieldFilter();

    const [bonusDialogOpen, setBonusDialogOpen] = useState(false);
    const [currentBonusDay, setCurrentBonusDay] = useState<{
        day: keyof Timesheet['dayDetails'] | null;
        dayLabel: string | null;
    }>({
        day: null,
        dayLabel: null
    });


    useEffect(() => {
        if (open && timesheet) {
            // Initialize dayDetails if not present
            const timesheetWithDefaults = {
                ...timesheet,
                dayDetails: timesheet.dayDetails || {
                    lundi: { hours: 0, quarters: 0, isDriver: false, isRecovery: false, vacationHours: 0 },
                    mardi: { hours: 0, quarters: 0, isDriver: false, isRecovery: false, vacationHours: 0 },
                    mercredi: { hours: 0, quarters: 0, isDriver: false, isRecovery: false, vacationHours: 0 },
                    jeudi: { hours: 0, quarters: 0, isDriver: false, isRecovery: false, vacationHours: 0 },
                    vendredi: { hours: 0, quarters: 0, isDriver: false, isRecovery: false, vacationHours: 0 },
                    samedi: { hours: 0, quarters: 0, isDriver: false, isRecovery: false, vacationHours: 0 },
                    dimanche: { hours: 0, quarters: 0, isDriver: false, isRecovery: false, vacationHours: 0 },
                },
            };
            setEditedTimesheet(timesheetWithDefaults);
        }
    }, [open, timesheet]);

    if (!editedTimesheet) return null;

    // Update day details hours and reflect in dayDetails for backwards compatibility
    const handleHoursChange = (day: keyof Timesheet['dayDetails'], value: number) => {
        if (!editedTimesheet.dayDetails) return;

        setEditedTimesheet({
            ...editedTimesheet,
            dayDetails: {
                ...editedTimesheet.dayDetails,
                [day]: {
                    ...editedTimesheet.dayDetails[day],
                    hours: value
                }
            }
        });
    };
    // Handle quarters change
    const handleQuartersChange = (day: keyof Timesheet['dayDetails'], value: number) => {
        if (!editedTimesheet.dayDetails) return;

        setEditedTimesheet({
            ...editedTimesheet,
            dayDetails: {
                ...editedTimesheet.dayDetails,
                [day]: {
                    ...editedTimesheet.dayDetails[day],
                    quarters: value
                }
            }
        });
    };

    // Handle driver status change
    const handleDriverChange = (day: keyof Timesheet['dayDetails'], checked: boolean) => {
        if (!editedTimesheet.dayDetails) return;

        setEditedTimesheet({
            ...editedTimesheet,
            dayDetails: {
                ...editedTimesheet.dayDetails,
                [day]: {
                    ...editedTimesheet.dayDetails[day],
                    isDriver: checked
                }
            }
        });
    };

    // Handle recovery day change
    const handleRecoveryChange = (day: keyof Timesheet['dayDetails'], checked: boolean) => {
        if (!editedTimesheet.dayDetails) return;

        setEditedTimesheet({
            ...editedTimesheet,
            dayDetails: {
                ...editedTimesheet.dayDetails,
                [day]: {
                    ...editedTimesheet.dayDetails[day],
                    isRecovery: checked
                }
            }
        });
    };

    // Handle week recovery flag change
    const handleWeekRecoveryChange = (checked: boolean) => {
        // etdit each day details to set isRecovery the selected value

        if (!editedTimesheet.dayDetails) return;
        const updatedDayDetails = { ...editedTimesheet.dayDetails };
        ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi'].forEach(day => {
            // Use type assertion to tell TypeScript this is a valid day key
            const dayKey = day as keyof Timesheet['dayDetails'];
            updatedDayDetails[dayKey] = {
                ...updatedDayDetails[dayKey],
                isRecovery: checked
            };
        });
        setEditedTimesheet({
            ...editedTimesheet,
            dayDetails: updatedDayDetails
        });

    };

    // Handle week recovery flag change
    const handleWeekDriverChange = (checked: boolean) => {
        // etdit each day details to set isRecovery the selected value

        if (!editedTimesheet.dayDetails) return;
        const updatedDayDetails = { ...editedTimesheet.dayDetails };

        ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi'].forEach(day => {
            // Use type assertion to tell TypeScript this is a valid day key
            const dayKey = day as keyof Timesheet['dayDetails'];
            updatedDayDetails[dayKey] = {
                ...updatedDayDetails[dayKey],
                isDriver: checked
            };
        });
        setEditedTimesheet({
            ...editedTimesheet,
            dayDetails: updatedDayDetails
        });

    };

    // Reset all hours to 0
    const handleResetAllHours = () => {
        if (!editedTimesheet.dayDetails) return;

        // Also update dayDetails
        const updatedDayDetails = { ...editedTimesheet.dayDetails };
        Object.keys(updatedDayDetails).forEach(day => {
            // Use type assertion to tell TypeScript this is a valid day key
            const dayKey = day as keyof Timesheet['dayDetails'];
            updatedDayDetails[dayKey] = {
                ...updatedDayDetails[dayKey],
                hours: 0
            };
        });

        setEditedTimesheet({
            ...editedTimesheet,
            dayDetails: updatedDayDetails
        });
    };

    // Reset a single day's hours to 0
    const handleResetDayHours = (day: keyof Timesheet['dayDetails']) => {
        handleHoursChange(day, 0);
    };

    const handleSave = () => {
        onSave(editedTimesheet);
    };

    const getWorkerMatricule = (): number | undefined => {
        // Find the worker in master data by name
        const worker = timesheet?.travailleur?.name ? timesheet.travailleur.name : '';
        const employee = masterData?.employees?.find(emp =>
            emp.name.toLocaleLowerCase().trim() === worker.toLocaleLowerCase().trim()
        );
        return employee ? employee.matricule : undefined;
    }

    const findMatriculeByName = (
        name: string | undefined,
        collection?: Array<{ name: string; matricule: number }>
    ): number | undefined => {
        if (!name) return undefined;

        const normalizedName = name.toLocaleLowerCase().trim();
        return collection?.find(item =>
            item.name.toLocaleLowerCase().trim() === normalizedName
        )?.matricule;
    };

    const formatOptionLabel = (
        option: { name?: string; matricule?: number | null } | string | null | undefined,
        collection?: Array<{ name: string; matricule: number }>
    ): string => {
        if (!option) return "";
        if (typeof option === 'string') return option;

        const matricule = option.matricule ?? findMatriculeByName(option.name, collection);
        return matricule !== undefined ? `${option.name} - ${matricule}` : (option.name || "");
    };

    const handleOpenBonusDialog = (day: keyof Timesheet['dayDetails'], dayLabel: string) => {
        setCurrentBonusDay({ day, dayLabel });
        setBonusDialogOpen(true);
    };

    const handleSaveBonuses = (bonuses: DayBonus[]) => {
        if (!currentBonusDay.day) return;

        const day = currentBonusDay.day;

        setEditedTimesheet({
            ...editedTimesheet,
            dayDetails: {
                ...editedTimesheet.dayDetails,
                [day]: {
                    ...editedTimesheet.dayDetails[day],
                    bonuses: [...bonuses] // Create new array to ensure state change is detected
                }
            }
        });
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                {(timesheet && (getWorkerMatricule())) &&
                    <Tooltip title={`${(getWorkerMatricule())}`}
                        enterTouchDelay={0}
                    >
                        <Chip
                            size="small"
                            label={`${((getWorkerMatricule())?.toString().length || 0) > 10 ?
                                (getWorkerMatricule())?.toString().substring(0, 8) + '...' :
                                (getWorkerMatricule())}`}
                            color={timesheet.id ? "primary" : "error"}
                            variant="filled"
                            sx={{
                                mr: 1,
                                height: 20,
                                fontSize: '0.7rem',
                                maxWidth: '110px',
                                '& .MuiChip-label': {
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                }
                            }}
                        />
                    </Tooltip>}
                {!isEditingAllowed ? 'Voir les détails' : 'Modification'} : {timesheet?.travailleur.name}

                {/* Is interim check */}
                <div style={{ display: "flex", flexDirection: "column", justifyContent: "space-between", alignSelf: "end" }}>
                    {timesheet?.travailleur.isInterim && (
                        <Chip
                            size="small"
                            label={'Intérimaire'}
                            sx={{ fontSize: '0.75rem', height: '20px', ml: 1, px: 0.5, mb: 1 }}
                            color="secondary"
                            icon={<People fontSize="small" />}
                            variant="outlined"
                        />
                    )}
                    {timesheet?.travailleur.interimCompanyName && <Chip
                        size="small"
                        label={timesheet?.travailleur.interimCompanyName}
                        sx={{ fontSize: '0.75rem', height: '20px', ml: 1 }}
                        color="default"
                        variant="outlined"
                        icon={<Apartment fontSize="small" />}
                    />}
                </div>

                {/* Foreman validation status */}
                {!isEditingAllowed && timesheet && (
                    <Chip
                        size="small"
                        color="warning"
                        icon={<TimerOff fontSize="small" />}
                        label={
                            // If only validated by foreman display the text related to it, if validated by Hr display a text related to it, and specify the name of each user and the date of validation
                            timesheet.foreman_validated && !timesheet.hr_validated
                                ? `Validé par le conducteur de travaux: ${timesheet.foreman_validator} on ${new Date(timesheet.foreman_validation_date || '').toLocaleDateString()}`
                                : timesheet.hr_validated
                                    ? `Validé par HR: ${timesheet.hr_validator} on ${new Date(timesheet.hr_validation_date || '').toLocaleDateString()}`
                                    : 'Non validée'
                        }
                        sx={{ ml: 2, fontSize: '0.75rem' }}
                    />
                )}
            </DialogTitle>
            <DialogContent>
                <Grid container spacing={3} sx={{ mt: 0.5 }}>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Semaine"
                            type="number"
                            value={editedTimesheet.weekNumber}
                            onChange={(e) => setEditedTimesheet({
                                ...editedTimesheet,
                                weekNumber: parseInt(e.target.value) || 0
                            })}
                            disabled={true}
                            InputProps={{
                                inputProps: { min: 1, max: 53 }
                            }}
                            helperText="Automatiquement calculeé depuis la Date"
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <LocalizationProvider dateAdapter={AdapterLuxon} adapterLocale="fr">
                            <DatePicker
                                label="Date"
                                format="dd/MM/yyyy"
                                value={
                                    editedTimesheet.timesheetDate
                                        ? (() => {
                                            const dt = DateTime.fromISO(editedTimesheet.timesheetDate);
                                            return dt.isValid ? dt : null;
                                        })()
                                        : null
                                } onChange={(newValue) => {
                                    if (newValue !== null) {
                                        setEditedTimesheet({
                                            ...editedTimesheet,
                                            timesheetDate: newValue.toISO()!, // keep ISO internally
                                            weekNumber: getCurrentWeekNumber(newValue.toJSDate())
                                        });
                                    }
                                }}
                                slots={{
                                    day: makeRenderWeekDay(
                                        hoveredDate,
                                        setHoveredDate,
                                        (dt: DateTime) => dt.toJSDate() // convert Luxon DateTime to JS Date
                                    ),
                                }}
                                slotProps={{
                                    textField: {
                                        fullWidth: true,
                                        required: true,
                                        error: !editedTimesheet.timesheetDate,
                                        helperText: !editedTimesheet.timesheetDate ? 'Ce champ est requis' : ''
                                    }
                                }}
                            />
                        </LocalizationProvider>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Autocomplete
                            id="chef-projet-autocomplete"
                            options={masterData?.foremans || []}
                            value={editedTimesheet.chefProjet || null}
                            onChange={(event, newValue) => {
                                setEditedTimesheet({
                                    ...editedTimesheet,
                                    chefProjet: newValue!
                                });
                            }}
                            getOptionLabel={(option) => formatOptionLabel(option, masterData?.foremans)}
                            isOptionEqualToValue={(option, value) => {
                                if (!option || !value) return false;
                                return option.matricule === value.matricule || option.name === value.name;
                            }}
                            filterOptions={multiFieldFilter}
                            disabled={!isEditingAllowed}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    fullWidth
                                    label="Conducteur de travaux"
                                    error={!editedTimesheet.chefProjet}
                                    helperText={!editedTimesheet.chefProjet ? 'Ce champ est requis' : ''}
                                />
                            )}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <Autocomplete
                            id="chef-chantier-autocomplete"
                            options={masterData?.workManagers || []}
                            value={editedTimesheet.chefChantier || null}
                            onChange={(event, newValue) => {
                                setEditedTimesheet({
                                    ...editedTimesheet,
                                    chefChantier: newValue
                                });
                            }}
                            getOptionLabel={(option) => formatOptionLabel(option, masterData?.workManagers)}
                            isOptionEqualToValue={(option, value) => {
                                if (!option || !value) return false;
                                return option.matricule === value.matricule || option.name === value.name;
                            }}
                            filterOptions={multiFieldFilter}
                            disabled={true}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    fullWidth
                                    label="Chef d\'équipes"
                                    error={!editedTimesheet.chefChantier}
                                    helperText={!editedTimesheet.chefChantier ? 'Ce champ est requis' : ''}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Autocomplete
                            id="worksite-autocomplete"
                            options={masterData?.sites || []}
                            value={editedTimesheet.worksite || null}
                            onChange={(event, newValue) => {
                                setEditedTimesheet({
                                    ...editedTimesheet,
                                    worksite: newValue
                                });
                            }}
                            getOptionLabel={(option) => formatOptionLabel(option, masterData?.sites)}
                            isOptionEqualToValue={(option, value) => {
                                if (!option || !value) return false;
                                return option.matricule === value.matricule || option.name === value.name;
                            }}
                            filterOptions={multiFieldFilter}
                            disabled={!isEditingAllowed}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    fullWidth
                                    label="Chantier"
                                    error={!editedTimesheet.worksite}
                                    helperText={!editedTimesheet.worksite ? 'this field is required' : ''}
                                />
                            )}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <Grid item xs={12} sm={6}>
                            <Autocomplete
                                id="travailleur-autocomplete"
                                options={masterData?.employees || []}
                                value={editedTimesheet.travailleur || null}
                                onChange={(event, newValue) => {
                                    setEditedTimesheet({
                                        ...editedTimesheet,
                                        travailleur: newValue
                                    });
                                }}
                                getOptionLabel={(option) => formatOptionLabel(option, masterData?.employees)}
                                isOptionEqualToValue={(option, value) => {
                                    if (!option || !value) return false;
                                    return option.matricule === value.matricule || option.name === value.name;
                                }}
                                filterOptions={multiFieldFilter}
                                disabled={true}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        fullWidth
                                        label="Employé"
                                        error={!editedTimesheet.travailleur}
                                        helperText={!editedTimesheet.travailleur ? 'Ce champ est requis' : ''}
                                    />
                                )}
                            />
                            {editedTimesheet.travailleur && (
                                <TextField
                                    fullWidth
                                    label="Métier de l'employé"
                                    value={(() => {
                                        // Find the employee in master data by matricule
                                        const employee = masterData?.employees?.find(emp =>
                                            emp.name.toLocaleLowerCase().trim() === editedTimesheet.travailleur.name.toLocaleLowerCase().trim()
                                        );
                                        // Return the role from master data or from the timesheet as fallback
                                        return employee?.role || editedTimesheet.travailleur.role || 'N/A';
                                    })()}
                                    disabled
                                    sx={{ mt: 2 }}
                                    InputProps={{
                                        readOnly: true,
                                    }}
                                />
                            )}
                        </Grid>
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Remarques"
                            placeholder="Remarques additionnelles...."
                            multiline
                            rows={3}
                            value={editedTimesheet.remark || ''}
                            onChange={(e) => setEditedTimesheet({
                                ...editedTimesheet,
                                remark: e.target.value
                            })}
                            disabled={!isEditingAllowed}
                            InputProps={{
                                sx: {
                                    backgroundColor: !isEditingAllowed ? 'rgba(0, 0, 0, 0.04)' : 'inherit'
                                }
                            }}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mb: 2,
                            mt: 2,
                            flexWrap: 'wrap',
                            gap: 2
                        }}>
                            <Typography variant="h6">
                                Détails
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={
                                                editedTimesheet.dayDetails &&
                                                ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi'].every(
                                                    day => editedTimesheet.dayDetails[day as keyof Timesheet['dayDetails']]?.isDriver
                                                )
                                            }
                                            onChange={(e) => handleWeekDriverChange(e.target.checked)}
                                            disabled={!isEditingAllowed}
                                            color="primary"
                                        />
                                    }
                                    label="Chauffeur en semaine"
                                />
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={
                                                editedTimesheet.dayDetails &&
                                                ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi'].every(
                                                    day => editedTimesheet.dayDetails[day as keyof Timesheet['dayDetails']]?.isRecovery
                                                )
                                            }
                                            onChange={(e) => handleWeekRecoveryChange(e.target.checked)}
                                            disabled={!isEditingAllowed}
                                            color="primary"
                                        />
                                    }
                                    label="Semaine de récupération"
                                />
                                {!!isEditingAllowed && (

                                    <Button
                                        color="error"
                                        variant="contained"
                                        size="small"
                                        startIcon={<TimerOff />}
                                        onClick={() => handleResetAllHours()}
                                    >
                                        Reinitialiser les heures a zéro
                                    </Button>
                                )}
                            </Box>
                        </Box>
                    </Grid>

                    {/* Day details accordions */}
                    {editedTimesheet.dayDetails && Object.keys(editedTimesheet.dayDetails).map((day) => {
                        const dayKey = day as keyof Timesheet['dayDetails'];
                        const dayDetails: DayDetails = editedTimesheet.dayDetails[dayKey];

                        if (!dayDetails) return null;

                        return (
                            <Grid item xs={12} key={day}>
                                <Paper
                                    sx={{
                                        p: 2,
                                        mb: 2,
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        borderLeft: dayDetails.isDriver || dayDetails.isRecovery ?
                                            '4px solid' : '1px solid',
                                        borderLeftColor: dayDetails.isDriver ?
                                            'primary.main' : dayDetails.isRecovery ?
                                                'secondary.main' : 'divider',
                                    }}
                                >
                                    <Grid container spacing={2} alignItems="center">
                                        <Grid item xs={12} sm={12} md={2}>
                                            <Typography variant="subtitle1" fontWeight="bold">
                                                {weekdayLabels[dayKey]} {editedTimesheet.timesheetDate &&
                                                    format(
                                                        addDays(
                                                            startOfWeek(new Date(editedTimesheet.timesheetDate), { locale: fr }),
                                                            ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi', 'dimanche'].indexOf(dayKey)
                                                        ),
                                                        'dd MMM',
                                                        { locale: fr }
                                                    )
                                                }
                                            </Typography>
                                        </Grid>

                                        <Grid item xs={12} sm={6} md={2}>
                                            <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                                                <TextField
                                                    select
                                                    fullWidth
                                                    size="small"
                                                    label="Heures"
                                                    value={dayDetails.hours}
                                                    onChange={(e) => handleHoursChange(dayKey, Number(e.target.value))}
                                                    disabled={!isEditingAllowed}
                                                >
                                                    {hoursOptions.map((hour) => (
                                                        <MenuItem key={hour} value={hour}>{hour}</MenuItem>
                                                    ))}
                                                </TextField>
                                                {!!isEditingAllowed && (
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleResetDayHours(dayKey)}
                                                        sx={{ ml: 0.5, mt: 1 }}
                                                    >
                                                        <Close fontSize="small" />
                                                    </IconButton>
                                                )}
                                            </Box>
                                        </Grid>

                                        <Grid item xs={12} sm={6} md={2}>
                                            <TextField
                                                select
                                                fullWidth
                                                size="small"
                                                label="Quarts"
                                                value={dayDetails.quarters || 0}
                                                onChange={(e) => handleQuartersChange(dayKey, Number(e.target.value))}
                                                disabled={!isEditingAllowed}
                                            >
                                                {quarterOptions.map((quarter) => (
                                                    <MenuItem key={quarter} value={quarter}>
                                                        {quarter === 0 ? '0' : `+${quarter}`}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                        </Grid>

                                        <Grid item xs={12} sm={6} md={2}>
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={Boolean(dayDetails.isDriver)}
                                                        onChange={(e) => handleDriverChange(dayKey, e.target.checked)}
                                                        disabled={!isEditingAllowed}
                                                        color="primary"
                                                        size="small"
                                                    />
                                                }
                                                label="Chauffeur"
                                            />
                                        </Grid>

                                        <Grid item xs={12} sm={6} md={2}>
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={Boolean(dayDetails.isRecovery)}
                                                        onChange={(e) => handleRecoveryChange(dayKey, e.target.checked)}
                                                        disabled={!isEditingAllowed}
                                                        color="secondary"
                                                        size="small"
                                                    />
                                                }
                                                label="Récupération"
                                            />
                                        </Grid>

                                        <Grid item xs={12} sm={6} md={2}>
                                            <Button
                                                size="small"
                                                variant="contained"
                                                color="success"
                                                onClick={() => handleOpenBonusDialog(dayKey, weekdayLabels[dayKey])}
                                                disabled={!isEditingAllowed}
                                                startIcon={<StarOutline />}
                                                sx={{ textTransform: 'none' }}
                                            >
                                                Autres codes salaires
                                                {dayDetails.bonuses && dayDetails.bonuses.length > 0 && (
                                                    <Chip
                                                        size="small"
                                                        label={dayDetails.bonuses.length}
                                                        color={dayDetails.bonuses.length > 0 ? "warning" : "success"}
                                                        sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                                                    />
                                                )}
                                            </Button>
                                        </Grid>
                                    </Grid>
                                </Paper>
                            </Grid>
                        );
                    })}

                    {/* Display creation and validation details */}
                    {timesheet?.id && (
                        <Grid item xs={12}>
                            <Divider sx={{ my: 2 }} />
                            <Typography variant="body2" color="textSecondary">
                                Crée le: {DateTime.fromISO(timesheet.created_at || '').toFormat("dd/MM/yyyy")} par : {timesheet.created_by || ''}
                            </Typography>
                            {timesheet.foreman_validation_date && (userRole?.toLocaleLowerCase() === 'Foreman'.toLocaleLowerCase() || userRole?.toLocaleLowerCase() === 'User'.toLocaleLowerCase() || isAdmin) && (
                                <Typography variant="body2" color="textSecondary">
                                    Validée par le conducteur de travaux le: {DateTime.fromISO(timesheet.foreman_validation_date).toFormat("dd/MM/yyyy")} par {timesheet.foreman_validator}
                                </Typography>
                            )}
                            {timesheet.hr_validation_date && (userRole?.toLocaleLowerCase() === 'Foreman'.toLocaleLowerCase() || userRole?.toLocaleLowerCase() === 'User'.toLocaleLowerCase() || isAdmin) && (
                                <Typography variant="body2" color="textSecondary">
                                    Validée par RH le : {DateTime.fromISO(timesheet.hr_validation_date).toFormat("dd/MM/yyyy")} par {timesheet.hr_validator}
                                </Typography>
                            )}
                        </Grid>
                    )}
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>
                    Annuler
                </Button>
                {!!isEditingAllowed && (
                    <Button
                        onClick={handleSave}
                        variant="contained"
                        color="primary"
                        disabled={
                            !isEditingAllowed ||
                            isRefreshing ||
                            !editedTimesheet.chefChantier ||
                            !editedTimesheet.chefProjet ||
                            !editedTimesheet.worksite ||
                            !editedTimesheet.travailleur ||
                            !editedTimesheet.timesheetDate ||
                            !editedTimesheet.weekNumber
                        }
                        startIcon={isRefreshing && <CircularProgress size={20} color="inherit" />}
                    >
                        {isRefreshing ? 'Enregistrement...' : 'Enregistrer'}
                    </Button>
                )}
            </DialogActions>

            {/** Bonus Dialog */}
            <BonusDialog
                open={bonusDialogOpen}
                onClose={() => setBonusDialogOpen(false)}
                day={currentBonusDay.day || ''}
                dayLabel={currentBonusDay.dayLabel || ''}
                worker={editedTimesheet.travailleur?.name || ''}
                bonuses={currentBonusDay.day ? (editedTimesheet.dayDetails[currentBonusDay.day]?.bonuses || []) : []}
                salaryCodes={masterData?.salaries || []}
                onSave={handleSaveBonuses}
                timesheetDate={editedTimesheet.timesheetDate}
            />
        </Dialog>
    );
};
